import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import querystring from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import LazyLoad from 'react-lazyload';
import { useDispatch } from 'react-redux';
import zenscroll from 'zenscroll';

import { addFilters, getRentals, setLocation } from '@/actions/rentals';
import { ArticleStructuredData } from '@/components/common/ArticleStructuredData';
import Breadcrumbs from '@/components/common/Breadcrumbs';
import { BreadcrumbStructuredData } from '@/components/common/BreadcrumbStructuredData';
import MaxWidth from '@/components/common/layout/MaxWidth';
import Sidebar from '@/components/common/Sidebar';
import SimilarRentalsCarousel from '@/components/common/SimilarRentalsCarousel';
import SocialShare from '@/components/common/SocialShare';
import { FAQs } from '@/components/localities/FAQs';
import { TFAQItem } from '@/components/localities/FAQs/types';
import { IVideoSchema } from '@/components/localities/Head/videoSchema';
import { VideoStructuredData } from '@/components/localities/Head/VideoStructuredData';
import Accommodations from '@/components/park-guide/Accommodations';
import Alerts from '@/components/park-guide/Alerts';
import Campsites from '@/components/park-guide/Campsites';
import Hero from '@/components/park-guide/Hero';
import SeasonalActivities from '@/components/park-guide/SeasonalActivities';
import Section from '@/components/park-guide/Section';
import TopNavigation from '@/components/park-guide/TopNavigation';
import Transportation from '@/components/park-guide/Transportation';
import { TOP_MENU_RVS } from '@/components/ui/SearchHeader/SearchHeader';
import { EHeaderTab } from '@/components/ui/SearchHeader/UniversalSearch/UniversalSearchContainer/menu';
import { ESearchFilters } from '@/constants/searchFilters';
import { useRouter } from '@/hooks/useRouter';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { EListingSource } from '@/services/analytics/listings/types';
import { trackRenterSearchedEvent } from '@/services/analytics/search';
import { ERentalType } from '@/services/analytics/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { CMSPageData, CMSPageMetadata, CMSPageType, ILocation } from '@/services/getCMSPageData';
import { trackEvent } from '@/services/track-event';
import filterEmptyValues from '@/utility/filterEmptyValues';
import { LocationGroupType } from '@/utility/surfacedLocation';

import Button, { ButtonSize, ButtonVariants } from '../switchback/Button/Button';
import css from './ParkTemplate.module.css';

export interface IParkAlert {
  category: string;
  description: string;
  id: string;
  parkCode: string;
  title: string;
  url: string;
}

type TSeason = 'winter' | 'fall' | 'spring' | 'summer';

interface ParkGuidePageMetadata extends CMSPageMetadata {
  page_type: CMSPageType.ParkGuide;
  images: { season: TSeason; url: string }[];
  transportation: Record<string, string>;
  accommodations: Record<string, string | boolean>;
  campgrounds: {
    description: string;
    location: ILocation;
    scheduling: string;
    title: string;
  }[];
  activities: {
    description: string;
    location: ILocation;
    season: string;
    title: string;
  }[];
  location: ILocation;
  nps_park_code: string;
  faqs?: TFAQItem[];
  video?: IVideoSchema;
}

export interface ParkGuidePageData extends CMSPageData {
  metadata: ParkGuidePageMetadata;
  alerts: IParkAlert[];
}

const GoogleParkMap = dynamic(() => import('@/components/maps/GoogleParkMap'), {
  ssr: false,
});

const ParkTemplate: React.FC<ParkGuidePageData> = props => {
  const {
    title,
    description,
    images,
    transportation,
    campgrounds,
    accommodations,
    activities,
    breadcrumbs,
    location,
    nps_park_code,
    url,
    faqs,
    video,
  } = props.metadata;

  const dispatch = useDispatch();
  const router = useRouter();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const useUSM = useExperimentIsEnabled(OptimizelyFlags.USM_FOR_GUIDE_PAGES);

  const intl = useIntl();
  const buttonMessage = intl.formatMessage({ defaultMessage: 'See all rentals', id: 'AI18Hm' });

  useEffect(() => {
    if (useUSM) {
      dispatch(
        setHeaderSearchFilter({
          address: title,
          [ESearchFilters.SKIP_DEFAULTS]: true,
        }),
      );
    }
  }, [dispatch, title, useUSM]);

  useEffect(() => {
    dispatch(setLocation(location.lat, location.lon));
    dispatch(getRentals({ count: 8 }));
    const defaultDuration = 300; // ms
    const edgeOffset = 70; // px
    zenscroll.setup(defaultDuration, edgeOffset);

    trackEvent({
      event: 'Locality : Viewed page',
      pageType: 'Park guide',
    });
  }, [dispatch, location]);

  const updateRentals = useCallback(
    async (start?: Date, end?: Date) => {
      // if we are using the USM, track the search event only (USM will redirect to search page)
      if (useUSM) {
        trackEvent({
          event: 'Content : ParkGuide : Performed search',
          location: title,
        });
        return;
      }

      setStartDate(start);
      setEndDate(end);

      if (!start || !end) {
        return null;
      }
      const requestParams = {
        'date[from]': dayjs(start).format('YYYY-MM-DD'),
        'date[to]': dayjs(end).format('YYYY-MM-DD'),
      };

      dispatch(addFilters(requestParams));
      dispatch(getRentals({ count: 8 }));

      const rentalsLocation = document.getElementById('rentals');
      rentalsLocation && zenscroll.to(rentalsLocation);
    },
    [dispatch, title, useUSM],
  );

  const goToSearchRentals = (title: string) => {
    const from = dayjs(startDate).format('YYYY-MM-DD');
    const to = dayjs(endDate).format('YYYY-MM-DD');
    let params: Record<string, unknown> = {
      address: title,
    };

    if (from && to && new Date(from) < new Date(to)) {
      params = {
        ...params,
        'date[from]': from,
        'date[to]': to,
      };
    }

    trackEvent({
      event: 'Content : ParkGuide : Clicked search all rental',
      location: title,
    });

    params = filterEmptyValues(params);
    const query = querystring.stringify(params);
    router.push(`/rv-search?${query}`);
  };

  return (
    <div
      className={`${useUSM ? 'relative flex flex-col items-center w-full' : ''} ${
        css.parksContainer
      }`}>
      {video && <VideoStructuredData video={video} />}
      {breadcrumbs && <BreadcrumbStructuredData breadcrumbs={breadcrumbs} />}
      <ArticleStructuredData
        article={{
          headline: title,
          description: description,
          url: url,
        }}
      />
      <Hero
        images={images}
        title={title}
        lockedSearchCategory={TOP_MENU_RVS}
        onSubmit={updateRentals}
      />
      <div className={css.stickyContainer}>
        <TopNavigation />

        <MaxWidth className="py-4">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </MaxWidth>

        <MaxWidth>
          <div className={css.split}>
            <div className={css.main}>
              <Section title="Introduction" html={description} id="accommodations" />
              <div id="alerts">
                <Alerts parkCode={nps_park_code} alerts={props.alerts} />
              </div>
            </div>

            <Sidebar>
              {!!title && !!url && (
                <SocialShare
                  url={url}
                  title={title}
                  label={intl.formatMessage({ defaultMessage: 'Share this Guide', id: 'QL5zxj' })}
                  type="guides"
                />
              )}
              <Accommodations accommodations={accommodations} />
            </Sidebar>
          </div>
        </MaxWidth>

        <MaxWidth>
          <h2 className="mb-4 highlight autoType800">RV Rentals in {title}</h2>
        </MaxWidth>
        <MaxWidth id="rentals" className="px-4 mb-6">
          <SimilarRentalsCarousel eventSource={EListingSource.GRID_LAYOUT} />
          <div className="w-64 m-auto mt-10">
            <Button
              onClick={() => {
                trackRenterSearchedEvent({
                  surfacedLocations: LocationGroupType.OTHER,
                  totalAdults: null,
                  totalChildren: null,
                  totalInfants: null,
                  locationQuery: title || null,
                  source: 'serp_search',
                  isADAAccessible: false,
                  isPetFriendly: false,
                  flexibleDateRange: null,
                  departureDate: null,
                  returnDate: null,
                  hasCategories: false,
                  selectedCategory: EHeaderTab.ALL,
                  towableYear: null,
                  towableMake: null,
                  towableModel: null,
                  rentalType: ERentalType.RV,
                  searchFilterCategory: null,
                  lastQuestionSeen: null,
                });

                goToSearchRentals(title);
              }}
              className="w-full"
              size={ButtonSize.large}
              variant={ButtonVariants.primaryOutlined}>
              {buttonMessage}
            </Button>
          </div>
        </MaxWidth>

        <div className={css.customMaxWidth}>
          <MaxWidth className={css.transportation} id="transportation">
            <Transportation transportation={transportation} />
          </MaxWidth>
        </div>

        <MaxWidth id="map">
          <h2 className="mb-4 highlight autoType800">Campgrounds and parking in {title}</h2>
        </MaxWidth>

        <div className={css.map}>
          <LazyLoad height={420} offset={200} once>
            <GoogleParkMap
              lat={location?.lat}
              lng={location?.lon}
              campgrounds={campgrounds}
              title={title}
            />
          </LazyLoad>
        </div>
        {campgrounds && campgrounds.length > 0 && (
          <div className={css.customMaxWidth}>
            <MaxWidth className={css.campsites} id="campsites">
              <Campsites campgrounds={campgrounds} title={title} />
            </MaxWidth>
          </div>
        )}
        {activities && activities.length > 0 && (
          <div className={css.customMaxWidth}>
            <MaxWidth className={css.seasonalActivities} id="activities">
              <SeasonalActivities activities={activities} title={title} />
            </MaxWidth>
          </div>
        )}
        {faqs && faqs.length > 0 && (
          <MaxWidth className="py-10">
            <h2 className="mb-6 text-2xl font-bold text-center">
              {intl.formatMessage(
                {
                  id: 'eKM75x',
                  defaultMessage: 'Frequently asked questions about {title}',
                  description: 'Park guide > FAQ section title',
                },
                { title },
              )}
            </h2>
            <FAQs items={faqs} />
          </MaxWidth>
        )}
      </div>
    </div>
  );
};

export default ParkTemplate;
